import React, {useState, useEffect} from "react";
import "./index.css";
import Textarea from "../../../layout/Textarea/Textarea";
import {NEUROBAKER, UI, newsletterLayoutTypes} from "../../../data/constants";
import {withDeployVersion} from "../../../libs/deployVersion";
import {checkIfIDE} from "../../../data/products/index";
import {checkProductInText} from "../../../libs/utils";
import BgTransformer from "../../../layout/BgTransformer";
import PixelationLayer from "../../../layout/PixelationLayer";
import {useSetting} from "../../../contexts/setting";
import {useEdits} from "../../../contexts/edits";
import {useBgUrl} from "hooks/useBgUrl";
import cn from "classnames";
import Icon from "../../../layout/Icon";
import ImageUpload from "../../../layout/ImageUpload";
import {WithTags} from "layout/Templates/WithTags";
import ContentEditable from "../../../layout/ContentEditable/ContentEditable";
import {useLogoUrl} from "../../../hooks/useLogoUrl";
import {KED_PRODUCTS_SET, HIDING_NAME_SET, SUBTITLE_SET, SUBTITLE2_SET} from "data/products/index";
import AirBanner, {isAirBanner} from "pages/Releases/AirBanner";

const InstagramFeed = ({id, zoom}) => {
  const {product, layoutOption, iconOption, type, channel} = useSetting();
  const {url: bgUrl} = useBgUrl();
  const {titles, subtitles, subtitles2, dates, productNames} = useEdits();
  const [showProductName, setShowProductName] = useState(true);
  const logoURL = useLogoUrl(id);
  const title = titles[id] || titles.main;
  const date = dates[id] || dates.main;
  const subtitle = subtitles[id] || subtitles.main;
  const subtitle2 = subtitles2[id] || subtitles2.main;
  const defaultProductName = product.shortLabel || product.label;
  const productName = productNames[id] || productNames.main || defaultProductName;
  const iconUrl = `${UI}/Icons/${iconOption}.svg`;
  const isNewsletter = newsletterLayoutTypes.includes(type.value);
  const newsletterClass = isNewsletter ? "newsletter" : "";
  const templateClass = cn(
    "template",
    channel.value === "releases" && product.value,
    `layout-${layoutOption}`,
    newsletterClass
  );
  const logoClass = cn("logo", {ide: checkIfIDE(product.value, type.value)});
  const logoProductClass = cn("logo-product", {ide: checkIfIDE(product.value, type.value)});
  const isTagsLayout = type.value === "new";
  const isReleasesKED = channel.value === "releases" && KED_PRODUCTS_SET.has(product.value);

  const logoProductURL = withDeployVersion(`${NEUROBAKER}/Logos/${product.value}.svg`);
  // Check if product name is in the text
  useEffect(() => {
    if (product && product.label) {
      const isProductInText = checkProductInText(product.label, title);
      setShowProductName(!isProductInText);
    }
  }, [product, title]);

  // JetBrains Air replaces the whole banner rather than restyling this one:
  // every channel that lists the product draws the same skin (src/data/offering.ts).
  if (isAirBanner(product.value)) return <AirBanner id={id} zoom={zoom} />;

  return (
    <>
      <div id={id} className={templateClass} style={{"--zoom-level": zoom}}>
        <div>
          <BgTransformer src={bgUrl} id={id} />
          <PixelationLayer />
          {(() => {
            switch (true) {
              case isReleasesKED:
                return (
                  <div className="content ked">
                    <main>
                      <div className="titles">
                        {SUBTITLE_SET.has(product.value) && (
                          <h2 className="subtitle">
                            <Textarea id={id} text={subtitle} />
                          </h2>
                        )}
                        <h1 className="title">
                          <ContentEditable id={id} text={title} variation="round" />
                        </h1>
                        {SUBTITLE2_SET.has(product.value) && (
                          <h2 className="subtitle2">
                            <Textarea id={id} text={subtitle2} />
                          </h2>
                        )}
                      </div>
                    </main>
                    <footer>
                      <div className={logoClass}>
                        <img src={logoURL} alt="background" />
                      </div>
                      {product.value !== "CMP" && (
                        <>
                          <div className={logoProductClass}>
                            <img src={logoProductURL} alt={product.label} />
                          </div>
                          <div className="product-name">
                            <ContentEditable id={id} text={productName} />
                          </div>
                        </>
                      )}
                    </footer>
                  </div>
                );
              case isTagsLayout:
                return <WithTags contentStyles={{height: "1200px"}} />;
              default:
                return (
                  <div className="content">
                    <div className="title-wrap">
                      <h1 className="title">
                        <Textarea id={id} text={title} />
                      </h1>
                      {isNewsletter && (
                        <div className="date">
                          <Textarea id={id} text={date} inline={true} />
                        </div>
                      )}
                      {[3, 4].includes(layoutOption) && (
                        <h2 className="subtitle">
                          <Textarea id={id} text={subtitle} />
                        </h2>
                      )}
                    </div>
                    {+layoutOption === 7 && (
                      <div className="icon-wrap">
                        <Icon bgUrl={iconUrl} />
                      </div>
                    )}
                    <header className="template-header">
                      <div className={logoClass}>
                        <img src={logoURL} alt="background" />
                      </div>
                      {HIDING_NAME_SET.has(product.value) || !showProductName ? (
                        ""
                      ) : (
                        <div className="product-name">
                          <ContentEditable id={id} text={productName} />
                        </div>
                      )}
                    </header>
                    {[5, 6].includes(+layoutOption) && (
                      <div className="upload-wrap">
                        <ImageUpload id={id} />
                      </div>
                    )}
                  </div>
                );
            }
          })()}
        </div>
      </div>
    </>
  );
};

export default InstagramFeed;
